import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Container, Flex } from '@/ui/layout';
import styled from 'styled-components';
import {
  ENABLED_QUESTS_TO_VIEW_STATUSES_ADMIN,
  ENABLED_STATUSES_INVESTOR,
  STATUS,
  STATUSES_IN_PROGRESS
} from '@/utils/static/kyc';
import { AddProductModal } from '@/modules/Product/modals';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { kycApi } from '@/api';
import { Typography } from 'antd';
import { TableProducts } from '@/components/common/organisms';
import { Button } from '@/components/common/atoms';
import { useSelector } from 'react-redux';
import { CreateButton } from '@/components/common/molecules';
import { usePopups } from '@/hooks';

const isEnabled = (accountStatus, questStatus, adminView) => {
  if (accountStatus !== 'ACTIVE' && !adminView) return false;
  return adminView
    ? ENABLED_QUESTS_TO_VIEW_STATUSES_ADMIN[questStatus]
    : ENABLED_STATUSES_INVESTOR[questStatus];
};

export const ProfileProductsView = ({
  data,
  investor,
  adminView = false,
  rejectedProducts,
  refetchProducts,
  referralView = false,
  referralId,
  referralToken
}) => {
  const { t } = useTranslation();
  const { showErrorToast } = usePopups();
  const navigate = useNavigate();
  const location = useLocation();
  const { isUserVerified } = useSelector((state) => state.investor);
  const [addProductVisible, setAddProductVisible] = useState(false);

  const { mutateAsync: checkProductStatus } = useMutation({
    mutationFn: adminView
      ? kycApi.checkProductStatusForAdmin
      : referralView
        ? kycApi.checkReferralProductStatus
        : kycApi.checkProductStatus
  });
  const productsSorted = useMemo(() => {
    if (data) {
      try {
        const productNew = [];
        const productInProgress = [];
        const productPassed = [];
        const productRejected = [];
        const productNeedChecking = [];
        const productDraft = [];

        data.data.forEach((product) => {
          const productStatus = product.status;
          const error =
            productStatus === STATUS.REJECTED && rejectedProducts
              ? rejectedProducts[product.id]
              : null;
          const formattedProduct = {
            ...product,
            error: error,
            enabled: isEnabled(investor.accountStatus, productStatus, adminView),
            onClick: (id) => {
              navigate(
                adminView
                  ? `/investor/${investor.id}/product/${id}`
                  : referralView
                    ? `/referral/${referralId}/product/${id}`
                    : `/product/${id}`,
                {
                  state: {
                    prevPath: location.pathname
                  }
                }
              );
            },
            onClickEdit: (id) => {
              navigate(
                adminView ? `/investor/${investor.id}/product/${id}/edit` : `/product/${id}`,
                {
                  state: {
                    prevPath: location.pathname
                  }
                }
              );
            },
            onClickCheck: async (id) => {
              try {
                const res = await checkProductStatus({
                  id: id,
                  ...(adminView && { investorId: investor.id }),
                  ...(referralView && referralToken && { token: referralToken })
                });
                if (res) {
                  refetchProducts();
                }
              } catch (e) {
                showErrorToast(e, true);
              }
            }
          };

          if (productStatus === STATUS.NEW) productNew.push(formattedProduct);
          else if (productStatus === STATUS.PASSED) productPassed.push(formattedProduct);
          else if (productStatus === STATUS.REJECTED) productRejected.push(formattedProduct);
          else if (STATUSES_IN_PROGRESS[productStatus]) productInProgress.push(formattedProduct);
          else if (productStatus === STATUS.NEED_CHECKING)
            productNeedChecking.push(formattedProduct);
          else if (productStatus === STATUS.DRAFT) productDraft.push(formattedProduct);
        });

        return {
          new: productNew,
          inProgress: productInProgress,
          passed: productPassed,
          rejected: productRejected,
          needChecking: productNeedChecking,
          draft: productDraft
        };
      } catch (e) {
        showErrorToast(e, true);
      }
    }

    return null;
  }, [data, rejectedProducts, adminView, investor, location]);

  if (productsSorted && investor) {
    return (
      <Container $paddingY={25} $marginBottom={40} style={{ position: 'relative' }}>
        <Flex align={'center'} justify='space-between' gap={20} wrap>
          <Typography.Title level={2}>{t('Products')}</Typography.Title>

          <Container $smdHidden>
            <Container $width={180}>
              <Button
                type='primary'
                text={t('Add product')}
                onClick={() => setAddProductVisible(true)}
                disabled={
                  adminView
                    ? false
                    : investor.accountStatus !== 'ACTIVE' ||
                      (investor.isAccessibleWithoutVerification ? false : !isUserVerified)
                }
              />
            </Container>
          </Container>

          <Container $hidden $smdVisible>
            <CreateButton
              onClick={() => setAddProductVisible(true)}
              disabled={
                adminView
                  ? false
                  : investor.accountStatus !== 'ACTIVE' ||
                    (investor.isAccessibleWithoutVerification ? false : !isUserVerified)
              }
            />
          </Container>
        </Flex>

        <Container $marginTop={36}>
          <TableProducts data={productsSorted} getFuncFromObj adminView={adminView} />
        </Container>

        <AddProductContainer>
          <AddProductModal
            visible={addProductVisible}
            onClose={() => setAddProductVisible(false)}
            user={adminView ? 'admin' : 'investor'}
            onSuccess={refetchProducts}
          />
        </AddProductContainer>
      </Container>
    );
  }

  return null;
};

const AddProductContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
`;
